import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "technical-project-management",
  kind: "horizontal",
  title: "Technical Project & Schedule Management",
  shortTitle: "Project Management",
  status: "draft",
  tier: "experience",
  seoTitle: "Technical Project & Schedule Management | GA",
  seoDescription:
    "Tool-agnostic technical project and schedule management across engineering programs, plant outages, software modernizations, and multi-discipline initiatives.",
  summary:
    "Tool-agnostic project and schedule management across complex engineering initiatives: critical-path modeling, multi-vendor coordination, and disciplined milestone delivery.",
  image: "/images/blog/operational-task-management-hero.webp",
  relatedCapabilitySlugs: [
    "automation-operations",
    "software-architecture-engineering",
  ],
  relatedIndustrySlugs: [
    "energy",
    "enterprise",
    "military",
    "healthcare",
    "agriculture",
  ],
  relatedPostSlugs: [
    "operational-task-management",
    "dashboard-design",
    "auditing-the-undocumented",
  ],
  engineering: [
    "Tool-agnostic integrated master schedule (IMS) architecture: Primavera P6, Microsoft Project, Excel, and Jira",
    "Detailed Work Breakdown Structure (WBS) development mapping multi-discipline engineering, procurement, and field construction",
    "Critical Path Method (CPM) scheduling, float analysis, and resource-leveled execution modeling",
    "Multi-contractor and vendor schedule reconciliation, progress curve tracking, and earned value analysis (EVA)",
    "Scope change control workflows, deviation tracking, and schedule recovery plans",
    "Executive milestone dashboards and operational risk matrices communicating true progress without corporate fluff",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Real Project Management Is Engineering, Rarely Done Right",
    },
    {
      type: "paragraph",
      text: "Across industries—from energy utilities and defense manufacturing to enterprise platforms and healthcare integrations—technical projects rarely fail from a lack of talent. They fail because project management is routinely reduced to passive status tracking, aesthetic Gantt charts, and tool dogmatism. When a schedule ignores task interdependencies, physical site realities, procurement lead times, and resource bottlenecks, it is not a plan; it is wishful thinking.",
    },
    {
      type: "heading",
      level: 2,
      text: "Tool-Agnostic Schedule Architecture",
    },
    {
      type: "paragraph",
      text: "High-performing execution does not depend on whether a team mandates Jira, Primavera P6, Microsoft Project, or custom Excel workbooks. What matters is modeling the true physics of the work. We design unified Work Breakdown Structures (WBS) and Integrated Master Schedules (IMS) that translate complex engineering dependencies into clear, accountable work packages that field crews, software developers, and executive sponsors can all execute against.",
    },
    {
      type: "heading",
      level: 2,
      text: "Critical Path Rigor and Multi-Vendor Coordination",
    },
    {
      type: "paragraph",
      text: "When managing major plant outages, capital equipment retrofits, or multi-vendor enterprise rollouts, the critical path is unforgiving. A delay in submittal approvals or cable delivery immediately pushes commercial operation. We maintain continuous critical-path analysis, identify float consumption early, reconcile conflicting contractor schedules, and implement aggressive recovery plans before minor variances turn into multimillion-dollar overruns.",
    },
  ],
  phases: {
    study: {
      focus:
        "Assess project scope, contractual deliverables, stakeholder commitments, existing schedules, and historical delivery bottlenecks.",
      produces: [
        "Project charter and governance framework review",
        "Contractual milestone and deliverable matrix",
        "Schedule baseline audit and critical-path gap analysis",
        "Stakeholder communication and cadence definition",
      ],
      fit: {
        good: [
          "Organizations undertaking complex, multi-discipline capital projects, outages, or platform modernizations",
          "Initiatives with multiple competing vendors, contractors, and internal teams requiring unified schedule alignment",
        ],
        poor: [
          "Teams seeking passive administrative record-keeping without authority to challenge schedule logic or dates",
          "Organizations unwilling to enforce scope change control or baseline discipline",
        ],
      },
    },
    "initial-design": {
      focus:
        "Build the Integrated Master Schedule (IMS), Work Breakdown Structure (WBS), and cross-discipline dependency logic.",
      produces: [
        "Hierarchical Work Breakdown Structure dictionary",
        "Tool-agnostic Integrated Master Schedule (P6, MS Project, or Jira/Excel)",
        "Resource loading and labor leveling profile",
        "Risk register with qualitative impact and mitigation pathways",
      ],
    },
    "detailed-design": {
      focus:
        "Establish baseline metrics, schedule change control procedures, vendor coordination cadences, and progress tracking mechanisms.",
      produces: [
        "Approved Schedule Baseline (Rev 0) and freeze protocol",
        "Contractor and vendor submittal tracking schedule",
        "Earned value management (EVM) reporting framework",
        "Weekly progress curve and milestone scorecard templates",
      ],
    },
    implementation: {
      focus:
        "Lead schedule execution, conduct weekly variance reviews, manage critical-path interventions, and report to steering committees.",
      produces: [
        "Active weekly schedule updates and float trend analysis",
        "Schedule deviation reports and recovery mitigation plans",
        "Executive milestone dashboards and operational risk reviews",
        "Project completion post-mortem and closeout documentation package",
      ],
    },
  },
};
